import { useState, useEffect, useRef } from "react";
import { Info, FileText, Download } from "lucide-react";

export default function MallaGrado({
  title,
  content,
  malla_enlinea,
  malla_presencial,
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // 1. Obtener todos los elementos hijos directos del contenedor de HTML
    const container = containerRef.current;
    const children = Array.from(container.children);

    let currentSection = [];
    const headers = [];

    // 2. Agrupar el contenido que va debajo de cada H2
    children.forEach((child) => {
      if (child.tagName === "H2") {
        // Guardamos el H2 anterior y su grupo de elementos
        headers.push({
          element: child,
          contentElements: [],
        });
      } else if (headers.length > 0) {
        // Si no es un H2, pertenece al grupo del último H2 encontrado
        headers[headers.length - 1].contentElements.push(child);
      }
    });

    // 3. Estilizar y preparar cada H2 y su respectivo contenido
    headers.forEach(({ element, contentElements }) => {
      // Estilos de Tailwind para que el H2 parezca un botón interactivo
      element.className = `
        cursor-pointer select-none flex justify-between items-center 
        text-lg font-bold text-[#004270] my-2 p-3 bg-gray-100  
        hover:bg-slate-200 rounded-xl transition-all duration-200
        after:content-['▼'] after:text-sm after:text-slate-500 after:transition-transform
      `;

      // Por defecto, ocultamos todos los elementos del contenido
      contentElements.forEach((el) => {
        el.style.display = "none";
        el.style.transition = "all 0.3s ease";
      });

      // Manejador del click para alternar (Toggle)
      const toggleAccordion = () => {
        const isCollapsed = contentElements[0]?.style.display === "none";

        // Alternamos la visibilidad del contenido
        contentElements.forEach((el) => {
          el.style.display = isCollapsed ? "block" : "none";
        });

        // Rotamos la flechita del H2
        if (isCollapsed) {
          element.classList.add("after:rotate-180");
          element.classList.add("bg-slate-200");
        } else {
          element.classList.remove("after:rotate-180");
          element.classList.remove("bg-slate-100");
        }
      };

      // Limpiamos listeners anteriores antes de agregar el nuevo (evita duplicados)
      element.removeEventListener("click", element._clickHandler);
      element._clickHandler = toggleAccordion;
      element.addEventListener("click", toggleAccordion);
    });

    // Cleanup al desmontar el componente
    return () => {
      headers.forEach(({ element }) => {
        if (element._clickHandler) {
          element.removeEventListener("click", element._clickHandler);
        }
      });
    };
  }, [content]); // Se vuelve a ejecutar si el contenido HTML cambia

  return (
    <section id="malla" className="py-24 bg-blue-50">
      <div className="max-w-5xl px-6 m-auto">
        <div className="text-center mx-6 mb-10 ">
          <div
            className="[&_h2]:font-bold  max-w-2xl m-auto"
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
        </div>
        <div
          ref={containerRef}
          className="
          /* --- CONTENEDOR GENERAL --- */
          bg-white p-6 md:p-10 rounded-3xl border border-slate-200 shadow-sm 
    [&_h2_strong]:m-0
      [&_h2_strong]:p-0
          [&_h2_strong]:text-lg     [&_h2_strong]:font-bold

          /* --- ESTILOS PARA LOS CICLOS (strong y p > strong) --- */
          [&_strong]:block 
          [&_strong]:text-[#004270] 
          [&_strong]:text-xl 
          [&_strong]:font-black 
          [&_strong]:tracking-tight 
          [&_strong]:mt-10 
          [&_strong]:mb-4 
          [&_strong]:pb-2 
          [&_strong]:border-b-2 
          [&_strong]:border-slate-100 
          [&_strong]:uppercase

          /* --- ESTILOS PARA LAS MATERIAS (h4) --- */
          [&_h4]:bg-gray-100
          [&_h4]:p-3                  
          [&_h4]:text-slate-700
           [&_h4]:rounded-3xl
          [&_h4]:my-3
          [&_h4]:mx-10
          [&_h4]:w-fit
          [&_h4]:text-sm
          [&_h4]:flex 
          [&_h4]:items-center
          [&_h4]:before:content-['🎓']
          [&_h4]:before:mr-2

           [&_ul]:px-6
        "
          dangerouslySetInnerHTML={{
            __html: content,
          }}
        />
      </div>

      <div className="max-w-5xl m-auto px-6">
              <div  >
        {/* Sección de PDFs */}
        <div className=" flex flex-col gap-4 mt-8">
          {/* Primer PDF */}
          {malla_enlinea && (
            <PdfCard
              url={malla_enlinea}
              title="Malla curricular modalidad en línea"
            />
          )}

          {/* Segundo PDF */}
          {malla_presencial && (
            <PdfCard
              url={malla_presencial}
              title="Malla curricular modalidad presencial"
            />
          )}
        </div>
      </div> </div>

    </section>
  );
}

function PdfCard({ url, title }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6">
        <div className="flex gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
            <FileText size={24} className="text-[#004270]" />
          </div>

          <div>
            <h3 className="font-bold">{title}</h3>
            <p className="text-sm text-slate-600 mt-1">
              Consulta el documento completo con la planificación académica del
              programa.
            </p>
          </div>
        </div>

        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-xl bg-[#004270] px-6 py-3 text-white font-semibold hover:bg-[#00345a] transition white-space-nowrap"
        >
          <Download size={18} />
          Descargar PDF
        </a>
      </div>
    </div>
  );
}
